/*!
 * CliniEco Sterilization & Compliance Log Generator — interactive layer
 * Version: v1.0.15 - 2026-09-22 - Owner: COO (theme layer)
 * v1.0.15 changes (2026-09-22, GEO #3878, mobile defect P0-1 second layer):
 *   v1.0.14 fixed the viewport fallback (innerWidth 980 -> 390) but the generated
 *   document still overflowed horizontally (scrollWidth 855 on a 390px screen).
 *   Root causes (element-level, measured): (1) the 10-column sheet table under
 *   table-layout:auto expands to its minimum content width (626px); (2) RCDSO source
 *   URLs do not break, driving <p class="note"> to scrollWidth 847 and the document to
 *   855. Fixed by table-layout:fixed (screen only), overflow-wrap:anywhere on cells and
 *   notes, and an .sheet-wrap overflow-x container around each sheet table. body was
 *   NOT given overflow-x:hidden - that would clip the rightmost column instead of
 *   letting the user reach it. Print layout (@page size:letter;margin:14mm) is untouched.
 *   Field set, storage, carry-forward, export, .ics and print behaviour are unchanged.
 * v1.0.14 changes (2026-09-22, GEO #3862, mobile defect P0-1):
 *   The two generated documents (printDoc / blankSheet) shipped without a viewport
 *   meta tag, so a phone laid them out at the default ~980px viewport and scaled the
 *   whole sheet down to about a third - legible on a desktop, unreadable on a phone.
 *   Both now declare width=device-width, which removes the ~980px fallback.
 *   CORRECTED in v1.0.15 (GEO #3878): a viewport alone does NOT make the sheet fit -
 *   a width:100% table under table-layout:auto still opens to its minimum content
 *   width (10 columns -> 626px), and long unbreakable tokens (RCDSO source URLs)
 *   pushed the document to 855px on a 390px phone. v1.0.15 adds table-layout:fixed
 *   + overflow-wrap and an overflow-x wrapper, so the sheet now fits and can still
 *   be scrolled sideways as a fallback; print
 *   layout is still governed by @page{size:letter;margin:14mm} and is unchanged.
 *   Field set, storage, carry-forward, export, .ics and print behaviour are unchanged.
 * v1.0.13 changes (2026-09-20, GEO #3381): one jurisdiction generator for every carrier.
 *   P0-1: the printed provenance line carried its own hard-coded Ontario sentence and
 *   retentionPlain() ignored the province, so a visitor who switched to Quebec / British
 *   Columbia (or any other jurisdiction) could still be handed Ontario's retention rule on
 *   the sheet and in the CSV. Print, CSV and .ics now all ask jurisPlainLine(), which is the
 *   only place in this file that states a jurisdiction requirement. The .ics was already
 *   gated on the switcher; it is now gated on the same function as the other two.
 *   P1: a statement about a regulator's rule now carries the date it was last checked against
 *   the source and where to read it - the two direct RCDSO PDFs on the sheet and in the CSV,
 *   the tool page on the .ics (which repeats in a calendar and is a prompt, not a record).
 *   P2: the page template shipped tool_version v1.0.10 while this build defaulted to v1.0.12.
 *   Both are now v1.0.13, so the version a visitor can read is the version that ran.
 *   Field set, storage, carry-forward, export and print behaviour are unchanged.
 * v1.0.10 changes (2026-09-17, GEO #3114): the retention period and the BI cadence are RCDSO
 *   requirements for Ontario DENTAL practice, and the retention sentence was printed in the CSV
 *   header, the on-page footer and the printed sheet for EVERY setting. Both lines are now emitted
 *   per setting: dental keeps the sourced sentence; every other setting says the figure has not been
 *   verified instead of borrowing the dental rule. Switching sector in-page now rebuilds what was
 *   already painted, so the previous sector's line cannot stay on screen. Field set, storage,
 *   export and print behaviour are otherwise unchanged from v1.0.9.
 * v1.0.9 changes: the one-click deliverables moved to the server-rendered hero, which is
 *   on screen before this file runs. This row is now only for the visitor's own records.
 *   demo()/blank() are exposed on window.cliniecoComplianceLog so the hero does not depend on
 *   a button existing in here. Recording, export and printing are unchanged from v1.0.8.
 * v1.0.8 changes (2026-09-15, benchmark §D P0 batch):
 *   P0-1 one-click deliverables. "Print a blank sheet" and "See a filled example" are now the
 *        first two actions, and a hero row repeats them above the fold; typing the fields is still
 *        available but is no longer the price of entry. New count-only event: tool_blank_sheet.
 *   P0-2 the province is stated before any requirement is shown. 13 jurisdictions, 1 verified
 *        against its own regulator, and the rest say so rather than print a number we have not read.
 *   P0-3 the requirement we state is printed on the sheet and written into the CSV header, with
 *        its source.
 *   The blank sheet is a template, not a record: no Record ID, no record count, stated on the sheet.
 * v1.0.7 changes (2026-09-15, GEO #2832 P0): sector narrowing + entry = identity. The page
 *   opens on Dental clinics; the other sectors' framework copy is built on demand and is NOT
 *   in the document until asked for. Recording, export and the printed sheet are byte-identical
 *   to v1.0.6 - this release only changes which framework the visitor is shown.
 * v1.0.2 changes (P0 batch, 2026-09-13): record dates use the LOCAL date (was UTC -> entries made
 *   after 20:00 America/Toronto were written with the next day's date); added PCD and Bowie-Dick /
 *   air-removal result fields; pressure unit now carried in the CSV header as well as the printed
 *   header; BI frequency guidance shown inside the form.
 * v1.0.3 changes: printed "N record(s)." now counts the rows actually printed (blank placeholder rows no
 *   longer inflate the count on the printed record).
 * v1.0.4 (2026-09-13): the P0 batch above (local date, PCD, Bowie-Dick, kPa in the CSV header, BI frequency
 *   line) shipped with the label bumped in the page template but not in this header. This header is now
 *   authoritative and matches the template's tool_version.
 * v1.0.5 changes (2026-09-14, GEO #2516 - GA4 instrumentation, no user-visible change): fires
 *   tool_export_csv / tool_export_empty / tool_print with integer counts only. Events never carry record
 *   content, lot numbers, names or any field value, so the page's "your data stays in the browser" promise
 *   still holds. Instrumentation is wrapped in try/catch and can never affect export or print.
 *   Fields, output, wording, storage and the printed sheet are byte-identical to v1.0.4.
 * Field set: 20 official fields from RCDSO IPAC Self Audit Review Form (v2) p.8-p.9
 *            + G5 [extension] + G6 [additional] (always labelled as NOT part of the official form)
 * v1.0.16 changes: new Group 7 - Cleaning equipment testing & maintenance (additional group).
 *   PHO IPAC Checklist for Dental Practice 1.2 requires a log for ultrasonic cleaners and
 *   washer-disinfectors; PHO 5.9 sets the frequency ("at least weekly or preferably each day it
 *   is used", quoted as published). Eight fields, carried into the same CSV export, the same
 *   printed sheet and the blank template. Groups 1-6 are untouched: their renderer, export,
 *   print and blank-sheet paths are byte-identical to v1.0.15.
 * v1.0.17 changes (device split for the PDF request):
 *   GA4 for 13-23 Sep shows mobile visitors press print once each (8/8) and never export CSV,
 *   while desktop visitors press it twice each (10/5). On a phone the request is "give me a file
 *   I can keep or send", not "print this". On mobile the button now reads Save as PDF (or print)
 *   and the generated document carries a line telling the visitor where the PDF is in the
 *   dialogue (iOS: Share -> Save to Files; Android: menu -> Print -> Save as PDF). That line is
 *   screen-only and never reaches paper. Desktop behaviour and wording are unchanged, as are the
 *   field set, export, .ics and print output.
 * No patient identifiers. Data stays in the browser (localStorage) unless the user exports it.
 * No hard-coded prices, no qualification wording (MDEL/Licensed/Certified) anywhere in this file.
 */
(function () {
  'use strict';

  var CFG = {};
  try {
    var el = document.getElementById('clinieco-tool-config');
    if (el && el.dataset) { CFG = el.dataset; }
  } catch (e) { CFG = {}; }
  var VERSION = CFG.version || 'v1.0.17';
  var MOUNT = CFG.mount || 'clinieco-log-tool';
  var STORE = 'clinieco-compliance-log-v1';

  /* ---------- carry-forward spec (P0-1) ----------
     Only values that genuinely repeat between consecutive loads of the same cycle type are
     carried forward. Everything else is deliberately NOT carried: cycle results must be judged
     per load, and a load number must never repeat. Keeping this list short is the whole point —
     a log that is copied blindly is no longer evidence of what happened. */
  var CARRY_FIELDS = ['sterilizer', 'cycle', 'time', 'temp', 'pressure', 'ohcw'];

  /* ---------- demand-signal keys (P0-4 / P0-5) ----------
     One flag per browser per signal: we are counting how many *different* people want these
     features, not how many times a button was pressed. */
  var SIGNAL_KEYS = { imp: 'clinieco_import_interest_v1', pro: 'clinieco_pro_interest_v1' };

  /* ---------- GA4 instrumentation (GEO #2516) ----------
     Events carry an event name, the fixed tool label and integer counts ONLY.
     Never send record content, lot numbers, operator names or any field value:
     the page tells the user the data stays in the browser, and analytics must
     not quietly break that promise. Failures are swallowed so that a missing or
     blocked gtag can never affect exporting or printing. */
  function track(name, params) {
    try {
      if (typeof window.gtag !== 'function') { return; }
      var p = { tool: 'compliance_log' };
      if (params) {
        for (var k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k)) { p[k] = params[k]; }
        }
      }
      window.gtag('event', name, p);
    } catch (e) { /* instrumentation must never affect the tool */ }
  }

  /* ---------- field spec (single source for the form, CSV and print) ---------- */
  var G = [
    { id: 'G1', title: 'Sterilization Load Log', note: 'For each load, log contains: (RCDSO 5542 p.9)', official: true,
      fields: [
        { k: 'date', label: 'Date', t: 'date', req: true },
        { k: 'sterilizer', label: 'Sterilizer #', t: 'text', req: true, datalist: 'sterilizers' },
        { k: 'loadNo', label: 'Load #', t: 'text', req: true, hint: 'e.g. 20260913-1' },
        { k: 'contents', label: 'Load contents', t: 'text', req: true, hint: 'contents only — never patient identifiers' },
        { k: 'cycle', label: 'Cycle', t: 'text', req: true, hint: 'per MIFU, e.g. gravity / pre-vacuum' },
        { k: 'time', label: 'Sterilization time', t: 'number', req: true, unit: 'min', min: 0 },
        { k: 'temp', label: 'Temperature', t: 'number', req: true, unit: '°C', min: 0 },
        { k: 'pressure', label: 'Pressure', t: 'number', req: true, unit: 'kPa', min: 0, hint: 'gauge pressure in kPa (psi × 6.895 = kPa)' },
        { k: 'ohcw', label: 'Name(s) of OHCW responsible for sterilization', t: 'text', req: true, datalist: 'staff' }
      ] },
    { id: 'G2', title: 'Service & Maintenance Log', note: 'RCDSO 5542 p.9', official: true,
      fields: [
        { k: 'sDate', label: 'Date(s) of service calls', t: 'date', req: true },
        { k: 'sProvider', label: 'Service provider', t: 'text', req: true },
        { k: 'sDone', label: 'Service performed', t: 'text', req: true }
      ] },
    { id: 'G3', title: 'Malfunction Episodes', note: 'RCDSO 5542 p.9', official: true,
      fields: [
        { k: 'mDate', label: 'Date', t: 'date', req: true },
        { k: 'mDesc', label: 'Description', t: 'text', req: true },
        { k: 'mAction', label: 'Action taken', t: 'text', req: true }
      ] },
    { id: 'G4', title: 'Package Label', note: 'RCDSO 5542 p.8 — one label per package', official: true,
      fields: [
        { k: 'lDate', label: 'Date', t: 'date', req: true },
        { k: 'lSterilizer', label: 'Sterilizer used', t: 'text', req: true, datalist: 'sterilizers' },
        { k: 'lLoadNo', label: 'Load or cycle number', t: 'text', req: true },
        { k: 'lContents', label: 'Contents (if not visible)', t: 'text', req: false },
        { k: 'lInitials', label: "OHCW's initials", t: 'text', req: true, maxlength: 6 }
      ] },
    { id: 'G5', title: 'Monitoring results', tag: 'additional', note: 'Required by the standard, field granularity defined by us',
      fields: [
        { k: 'ci', label: 'CI result (Type 1/4/5/6)', t: 'select', opts: ['', 'pass', 'fail'] },
        { k: 'bi', label: 'BI result', t: 'select', opts: ['', 'pass', 'fail'] },
        { k: 'biCtrl', label: 'BI control result', t: 'select', opts: ['', 'pass', 'fail'] },
        { k: 'pcd', label: 'PCD result (process challenge device)', t: 'select', opts: ['', 'pass', 'fail', 'n/a'],
          hint: 'where a PCD is used — the standard requires PCD results to be verified and recorded' },
        { k: 'bowie', label: 'Bowie-Dick / air-removal test result', t: 'select', opts: ['', 'pass', 'fail', 'n/a'],
          hint: 'start of each day, before the first load — for pre-vacuum (class B) sterilizers' },
        { k: 'verifiedBy', label: 'Read / verified by', t: 'text' }
      ] },
    { id: 'G6', title: 'Traceability', tag: 'additional', note: 'Not part of the official form',
      fields: [
        { k: 'lot', label: 'Product / consumable lot #', t: 'text' },
        { k: 'expiry', label: 'Expiry date', t: 'date' },
        { k: 'implant', label: 'Implant load?', t: 'select', opts: ['', 'no', 'yes'] },
        { k: 'implantLot', label: 'Implant / device lot #', t: 'text', showIf: 'implant=yes' }
      ] },
    { id: 'G7', title: 'Cleaning equipment testing & maintenance', tag: 'additional',
      note: 'PHO IPAC Checklist 1.2 (dental reprocessing) requires a log for ultrasonic cleaners and washer-disinfectors',
      fields: [
        { k: 'cDate', label: 'Date', t: 'date', req: true },
        { k: 'cEquip', label: 'Equipment type', t: 'select', req: true,
          opts: ['', 'Ultrasonic cleaner', 'Washer-disinfector'] },
        { k: 'cUnit', label: 'Equipment ID / unit #', t: 'text' },
        { k: 'cMethod', label: 'Test method', t: 'select', req: true,
          opts: ['', 'Foil test', 'Commercial test method', 'Other (per MIFU)'] },
        { k: 'cResult', label: 'Test result', t: 'select', req: true, opts: ['', 'pass', 'fail'] },
        { k: 'cSol', label: 'Cleaning solution changed (date / lot)', t: 'text' },
        { k: 'cPm', label: 'Preventive maintenance performed', t: 'text' },
        { k: 'cBy', label: 'Performed / verified by', t: 'text' }
      ] }
  ];

  var LOAD_GROUPS = ['G1', 'G5', 'G6'];   /* one row per load */
  var ROW_GROUPS = { G2: 'service', G3: 'malfunctions', G4: 'labels', G7: 'cleaning' };

  /* ---------- state ---------- */
  var S = { v: 1, updatedAt: '', loads: [], service: [], malfunctions: [], labels: [], cleaning: [] };

  function uid() { return 'r' + Math.random().toString(36).slice(2, 9); }

  function load() {
    try {
      var raw = window.localStorage.getItem(STORE);
      if (raw) {
        var d = JSON.parse(raw);
        if (d && d.v === 1) { S = d; }
      }
    } catch (e) { /* private mode / quota — tool still works, just not persisted */ }
    ['loads', 'service', 'malfunctions', 'labels', 'cleaning'].forEach(function (k) { if (!Array.isArray(S[k])) { S[k] = []; } });
  }

  function save() {
    S.updatedAt = new Date().toISOString();
    try { window.localStorage.setItem(STORE, JSON.stringify(S)); } catch (e) { /* ignore */ }
    var b = document.getElementById('clog-saved');
    if (b) { b.textContent = 'Saved in this browser · ' + new Date().toLocaleTimeString(); }
  }


  /* v1.0.17 — device split. The GA4 read was unambiguous: on mobile each visitor presses
     print exactly once (8 presses / 8 users) and never exports CSV, while on desktop each
     visitor presses it twice (10 / 5). A phone visitor wants a file they can keep or send,
     not a sheet coming out of a printer. So on mobile we lead with the PDF wording and tell
     them where the PDF actually is; desktop keeps the plain print wording and behaviour.
     Detection is by user agent, not by width: an iPad in landscape is wide and still mobile. */
  function isMobileUA() {
    var ua = (navigator && navigator.userAgent) || '';
    if (/iPad|iPhone|iPod/.test(ua)) { return true; }
    if (/Android/i.test(ua) && /Mobile/i.test(ua)) { return true; }
    if (/Windows Phone|IEMobile|Opera Mini|Mobile Safari/i.test(ua)) { return true; }
    /* iPadOS 13+ reports a Mac UA but is still touch-only. */
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) { return true; }
    return false;
  }

  /* Shown inside the generated document (screen only — never on the printed page).
     The print dialogue opens on top of it, so the wording has to survive being seen
     only after the dialogue is dismissed. */
  function saveAsPdfHintHtml() {
    if (!isMobileUA()) { return ''; }
    var ios = /iPad|iPhone|iPod/.test((navigator && navigator.userAgent) || '') ||
              (/Macintosh/.test((navigator && navigator.userAgent) || '') && navigator.maxTouchPoints > 1);
    var how = ios
      ? 'tap the <strong>Share</strong> icon, then choose <strong>Save to Files</strong> or <strong>Mail</strong>'
      : 'open the <strong>menu</strong>, choose <strong>Print</strong>, then set the destination to <strong>Save as PDF</strong>';
    return '<p class="note clog-pdf-hint" style="border:1.5pt solid #000;padding:3mm;background:#fffbe6" id="clog-pdf-hint">' +
      '<strong>To keep this as a PDF:</strong> in the dialogue that just opened, ' + how + '. ' +
      'If the dialogue did not appear, use your browser\u2019s Print or Share option. ' +
      'Nothing has been sent anywhere \u2014 this page is generated in your browser.</p>';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* Record dates must be LOCAL dates. A UTC date writes tomorrow's date for every entry made after
     20:00 America/Toronto — evening and night entries are a large share of sterilizer logs. */
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function localDate(d) {
    d = d || new Date();
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }
  function localStamp(d) {
    d = d || new Date();
    var off = -d.getTimezoneOffset(), sign = off >= 0 ? '+' : '-';
    return localDate(d) + ' ' + pad2(d.getHours()) + ':' + pad2(d.getMinutes()) + ':' + pad2(d.getSeconds()) +
      ' local (UTC' + sign + pad2(Math.floor(Math.abs(off) / 60)) + ':' + pad2(Math.abs(off) % 60) + ')';
  }

  var TODAY = localDate();

  /* a row counts as real data only if it holds something beyond the auto-filled date */
  function hasData(r, list) {
    var fields = list || allFields();
    return fields.some(function (x) {
      var v = r[x.f.k];
      if (v == null || String(v).length === 0) { return false; }
      if (x.f.t === 'date' && String(v) === TODAY) { return false; }
      return true;
    });
  }

  /* ---------- rendering ---------- */
  function fieldHtml(g, f, rowId, val) {
    var id = 'f_' + rowId + '_' + f.k;
    var v = val == null ? '' : val;
    var req = f.req ? ' required' : '';
    var a = ' data-k="' + f.k + '" data-row="' + rowId + '" data-g="' + g.id + '" id="' + id + '"';
    var lbl = '<label for="' + id + '">' + esc(f.label) + (f.req ? ' <span class="clog-req" aria-hidden="true">*</span>' : '') + '</label>';
    var ctl;
    if (f.t === 'select') {
      ctl = '<select' + a + req + '><option value=""></option>';
      f.opts.slice(1).forEach(function (o) { ctl += '<option value="' + esc(o) + '"' + (String(v) === o ? ' selected' : '') + '>' + esc(o) + '</option>'; });
      ctl += '</select>';
    } else {
      ctl = '<input type="' + f.t + '"' + a + req +
        (f.unit ? ' step="any"' : '') + (f.min != null ? ' min="' + f.min + '"' : '') +
        (f.maxlength ? ' maxlength="' + f.maxlength + '"' : '') +
        (f.datalist ? ' list="clog-' + f.datalist + '"' : '') +
        ' value="' + esc(v) + '">';
    }
    var hint = f.hint ? '<p class="clog-hint">' + esc(f.hint) + '</p>' : '';
    var unit = f.unit ? '<span class="clog-unit">' + esc(f.unit) + '</span>' : '';
    return '<div class="clog-field" data-field="' + f.k + '">' + lbl + '<div class="clog-ctl">' + ctl + unit + '</div>' + hint + '</div>';
  }

  function rowHtml(row, kind) {
    var out = '';
    G.forEach(function (g) {
      var inLoad = LOAD_GROUPS.indexOf(g.id) > -1;
      if (inLoad) { return; }
      var isThis = ROW_GROUPS[g.id] === kind;
      if (!isThis) { return; }
      g.fields.forEach(function (f) { out += fieldHtml(g, f, row.id, row[f.k]); });
    });
    return out;
  }

  function render() {
    var host = document.getElementById(MOUNT);
    if (!host) { return; }

    var html = '';
    html += '<div class="clog-head">' +
      '<p class="clog-note"><strong>Only cycle, equipment and consumable data.</strong> Do not enter patient names or other patient identifiers.</p>' +
      /* v1.0.9: the two one-click deliverables now live in the hero above, server-rendered, so they
         are visible before this script runs. Keeping a second copy here made the same pair of
         buttons appear twice on the first screen. This row is for working with your own records. */
      '<div class="clog-actions">' +
      '<button type="button" class="clog-btn clog-primary" data-act="add-load">+ Add load record</button>' +
      '<button type="button" class="clog-btn" data-act="print">' + (isMobileUA()
        ? 'Save as PDF (or print)'
        : 'Print my log / Save as PDF') + '</button>' +
      '<button type="button" class="clog-btn" data-act="csv">Download CSV (no sign-up)</button>' +
      '<button type="button" class="clog-btn clog-quiet" data-act="wipe">Clear all</button>' +
      '</div><p class="clog-saved" id="clog-saved"></p></div>';

    html += '<datalist id="clog-staff"></datalist><datalist id="clog-sterilizers"></datalist>';

    html += '<section class="clog-sec" aria-labelledby="clog-h-loads"><h3 id="clog-h-loads">Load records <span class="clog-count" data-count="loads"></span></h3>';
    html += '<p class="clog-hint">Field names and order follow the RCDSO IPAC Self Audit Review Form (v2), p.9. Groups marked “extension” / “additional” are ours and are not part of the official form.</p>';
    if (!S.loads.length) { html += '<p class="clog-empty">No load records yet. Print a blank sheet to fill in by hand, or add the first load &mdash; it takes about 30 seconds.</p>'; }
    S.loads.forEach(function (r, i) {
      var inner = '';
      G.forEach(function (g) {
        if (LOAD_GROUPS.indexOf(g.id) === -1) { return; }
        var sub = '';
        g.fields.forEach(function (f) { sub += fieldHtml(g, f, r.id, r[f.k]); });
        var formHint = (g.id === 'G5') ? biHintHtml() : '';
        inner += '<div class="clog-sub" data-g="' + g.id + '" data-official="' + (g.official ? '1' : '0') + '">' +
          '<p class="clog-subhead">' + esc(g.title) +
          (g.official ? ' <span class="clog-tag clog-official">official \u00b7 RCDSO 5542 p.9</span>'
                      : ' <span class="clog-tag">' + (g.id === 'G5' ? 'extension' : 'additional') + '</span>' +
                        ' \u2014 these additional fields are not part of the RCDSO Self Audit Review Form') +
          '</p>' + formHint + '<div class="clog-grid">' + sub + '</div></div>';
      });
      var rowhead = '<div class="clog-rowhead"><span class="clog-rowidx">#' + (i + 1) + '</span><span class="clog-rowbtns">';
      if (i > 0) {
        rowhead += '<button type="button" class="clog-btn clog-quiet" data-act="copy-prev" data-row="' + r.id + '">Carry forward from #' + i + '</button>';
      }
      rowhead += '<button type="button" class="clog-btn clog-quiet" data-act="del" data-row="' + r.id + '" aria-label="Delete this load record">Remove</button></span></div>';
      if (r.carriedFrom) {
        /* Visible provenance marker. Deliberately NOT part of the field spec, so it can never
           appear in the CSV or on the printed sheet - it is a prompt to verify, not a record. */
        rowhead += '<p class="clog-carry-note">Values carried forward from load #' + esc(String(r.carriedFrom)) +
          ' - <strong>check each one against the readings taken for this load.</strong> Load number and results are not carried.</p>';
      } else if (r.carryEmpty) {
        /* The source row was empty, so nothing moved. Say so instead of claiming a carry. */
        rowhead += '<p class="clog-carry-note clog-carry-empty">Load #' + esc(String(r.carryEmpty)) +
          ' has no entered values yet, so nothing was carried. Fill it in, then carry again.</p>';
      }
      html += '<div class="clog-row" data-row="' + r.id + '">' + rowhead + inner + '</div>';
    });
    html += '</section>';

    html += '<section class="clog-sec" aria-labelledby="clog-h-svc"><h3 id="clog-h-svc">Service &amp; maintenance log <span class="clog-count" data-count="service"></span></h3>';
    html += renderRows('service', 'G2');
    html += '</section>';

    html += '<section class="clog-sec" aria-labelledby="clog-h-mf"><h3 id="clog-h-mf">Malfunction episodes <span class="clog-count" data-count="malfunctions"></span></h3>';
    html += renderRows('malfunctions', 'G3');
    html += '</section>';

    html += '<section class="clog-sec" aria-labelledby="clog-h-lb"><h3 id="clog-h-lb">Package labels <span class="clog-count" data-count="labels"></span></h3>';
    html += renderRows('labels', 'G4');
    html += '</section>';
    html += '<section class="clog-sec" aria-labelledby="clog-h-cln"><h3 id="clog-h-cln">Cleaning equipment testing &amp; maintenance <span class="clog-count" data-count="cleaning"></span></h3>';
    html += g7HintHtml();
    html += renderRows7();
    html += '</section>';

    html += '<p class="clog-foot">Fields 1–20 follow the RCDSO IPAC Self Audit Review Form (v2), p.8–p.9. ' +
      esc(retentionPlain()) + ' ' +
      'This tool is for reference only and does not constitute compliance advice — always refer to the official source. ' +
      'CliniEco Medical · https://clinieco.ca · tool ' + esc(VERSION) + ' · generated ' + esc(localDate()) + '</p>';
    html += remindersHTML();

    /* Demand signals. Deliberately at the very bottom of the output area, below the record
       itself - never in the first screen, never a banner or a popup. The tool's value is that
       it asks for nothing; these two lines only appear once a visitor has scrolled the sheet. */
    html += '<div class="clog-interest" id="clog-interest">' +
      '<p class="clog-interest-q">Running a service for several clinics? ' +
      '<button type="button" class="clog-link" data-act="sig-pro">Tell us</button></p>' +
      '<p class="clog-interest-q">Already keep a sterilizer log somewhere else? ' +
      '<button type="button" class="clog-link" data-act="sig-imp">Tell us you would want to import it</button></p>' +
      '<p class="clog-interest-q">The sheet is half the record — pair it with your spore testing. Clinics can get a <a class="clog-link" href="https://clinieco.ca/pages/bi-sample-request">free 5-pack of biological indicators →</a></p>' +
      '<p class="clog-interest-note" id="clog-signal-note" role="status" aria-live="polite"></p>' +
      '</div>';

    host.innerHTML = html;
    paintShowIf();
    updateCounts();
  }

    /* ---------- 方案A：提醒（2026-09-19 胡总放行）----------
       Constraint kept deliberately: the tool asks for NOTHING — no signup, no email,
       no popup. This block adds one quiet line with two calendar downloads.
       Copy for both .ics files is GEO's final text (#3276) — do not paraphrase.
       Spec points that matter: ALL-DAY events (VALUE=DATE), NO alarm, NO VALARM,
       NO DTEND. A daily reminder with an alarm gets deleted within two weeks. */
    var TOOL_LINK = 'https://clinieco.ca/pages/compliance-log-center';
    function icsStamp(d) {
      function p(n) { return (n < 10 ? '0' : '') + n; }
      return d.getUTCFullYear() + p(d.getUTCMonth() + 1) + p(d.getUTCDate()) + 'T' +
        p(d.getUTCHours()) + p(d.getUTCMinutes()) + p(d.getUTCSeconds()) + 'Z';
    }
    function icsDate(d) {
      function p(n) { return (n < 10 ? '0' : '') + n; }
      return d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate());
    }
    function icsEsc(s) {
      return String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
    }
    function buildICS(freq) {
      var daily = (freq === 'daily');
      /* GEO #3381 P0-1: the .ics is a DOWNLOAD, built at the moment of the click, so it must read
         the jurisdiction then - it never re-renders. Same generator as the sheet and the CSV; the
         lead sentence above is GEO's copy (#3276) and is unchanged. */
      var jl = jurisPlainLine('short');
      var desc = daily
        ? "Run the BI control and log today's sterilizer results.\n\nFree blank log sheet (no signup): " + TOOL_LINK +
          (jl ? "\n\n" + jl : "")
        : "New month - start a fresh log sheet and file last month's. Free blank sheet (no signup): " + TOOL_LINK +
          (jl ? "\n\n" + jl : "");
      var lines = [
        'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//CliniEco Medical//Sterilization Log//EN',
        'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        'UID:clinieco-' + (daily ? 'daily' : 'monthly') + '@clinieco.ca',
        'DTSTAMP:' + icsStamp(new Date()),
        'DTSTART;VALUE=DATE:' + icsDate(new Date()),
        'DURATION:P1D',
        'SUMMARY:' + icsEsc(daily ? 'CliniEco Medical - daily sterilization log'
                                  : 'CliniEco Medical - monthly log archiving'),
        'DESCRIPTION:' + icsEsc(desc),
        'RRULE:' + (daily ? 'FREQ=DAILY' : 'FREQ=MONTHLY;BYMONTHDAY=1'),
        'END:VEVENT', 'END:VCALENDAR', ''
      ];
      return lines.join('\r\n');
    }
    function downloadICS(freq) {
      try {
        var blob = new Blob([buildICS(freq)], { type: 'text/calendar;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = (freq === 'daily' ? 'clinieco-daily-log-reminder.ics'
                                       : 'clinieco-monthly-log-reminder.ics');
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
      } catch (e) { /* a reminder must never break the tool */ }
      track(freq === 'daily' ? 'hook_ics_daily' : 'hook_ics_monthly');
    }
    /* Requirement lead-in is GATED on the switchers (GEO #3324): the tool must never show an
       Ontario-dental requirement while the panel above says "not yet verified for this choice". */
    function remLead() {
      var base = "Want a nudge so the log doesn't slip?";
      return (currentSetting() === 'dental' && currentJuris() === JURIS_VERIFIED)
        ? 'Ontario requires a biological indicator control test once daily for each type of cycle. ' + base
        : base;
    }
    function syncRemLead() {
      var rr = document.getElementById('clog-rem-req');
      if (rr) { rr.textContent = remLead(); }
    }
    function remindersHTML() {
      return '<div class="clog-muted" style="margin-top:14px">' +
        '<p><span id="clog-rem-req">' + esc2(remLead()) + '</span> ' +
        '<button type="button" class="clog-link" data-act="ics-daily">Add a daily log reminder (.ics)</button> &middot; ' +
        '<button type="button" class="clog-link" data-act="ics-monthly">Add a monthly filing reminder (.ics)</button></p>' +
        '</div>';
    }
  function renderRows(kind, gid) {
    var g = G.filter(function (x) { return x.id === gid; })[0];
    var rows = S[kind];
    var html = '<button type="button" class="clog-btn" data-act="add-' + kind + '">+ Add row</button>';
    if (!rows.length) { return '<p class="clog-empty">Nothing recorded yet.</p>' + html; }
    rows.forEach(function (r, i) {
      html += '<div class="clog-row" data-row="' + r.id + '"><div class="clog-rowhead"><span class="clog-rowidx">#' + (i + 1) + '</span>' +
        '<button type="button" class="clog-btn clog-quiet" data-act="del" data-kind="' + kind + '" data-row="' + r.id + '">Remove</button></div>' +
        '<p class="clog-subhead">' + esc(g.title) + ' <span class="clog-tag clog-official">official \u00b7 RCDSO 5542 p.' + (gid === 'G4' ? '8' : '9') + '</span></p>' +
        '<div class="clog-grid">';
      g.fields.forEach(function (f) { html += fieldHtml(g, f, r.id, r[f.k]); });
      html += '</div></div>';
    });
    return html + html0(kind);
  }
  function html0(kind) { return '<button type="button" class="clog-btn" data-act="add-' + kind + '">+ Add row</button>'; }

  /* Group 7 renderer — deliberately separate from renderRows(), which hard-codes the
     "official · RCDSO 5542 p.9" tag for the three RCDSO groups. Group 7 is an additional
     group (PHO IPAC Checklist 1.2), so it carries the additional tag instead. */
  function renderRows7() {
    var g = G.filter(function (x) { return x.id === 'G7'; })[0];
    var rows = S.cleaning;
    var html = '<button type="button" class="clog-btn" data-act="add-cleaning">+ Add row</button>';
    if (!rows.length) { return '<p class="clog-empty">Nothing recorded yet.</p>' + html; }
    rows.forEach(function (r, i) {
      html += '<div class="clog-row" data-row="' + r.id + '"><div class="clog-rowhead"><span class="clog-rowidx">#' + (i + 1) + '</span>' +
        '<button type="button" class="clog-btn clog-quiet" data-act="del" data-kind="cleaning" data-row="' + r.id + '">Remove</button></div>' +
        '<p class="clog-subhead">' + esc(g.title) + ' <span class="clog-tag">additional</span></p>' +
        '<div class="clog-grid">';
      g.fields.forEach(function (f) { html += fieldHtml(g, f, r.id, r[f.k]); });
      html += '</div></div>';
    });
    return html + html0('cleaning');
  }

  /* PHO 5.9 frequency, quoted as published. Never reworded into "weekly is enough":
     the text is "at least weekly or preferably each day it is used". */
  function g7HintHtml(rowId) {
    var days = '';
    if (rowId) {
      var best = null;
      (S.cleaning || []).forEach(function (r) {
        if (r.id === rowId || !r.cDate) { return; }
        var d = new Date(r.cDate);
        if (isNaN(d.getTime())) { return; }
        if (!best || d > best) { best = d; }
      });
      if (best) {
        var diff = Math.floor((Date.now() - best.getTime()) / 86400000);
        if (diff >= 0) { days = ' Last recorded test: ' + diff + ' day' + (diff === 1 ? '' : 's') + ' ago.'; }
      }
    }
    var base = 'Cleaning equipment (ultrasonic cleaner / washer-disinfector) must be tested and documented ' +
      '"at least weekly or preferably each day it is used" (PHO IPAC Checklist 5.9), as per the manufacturer’s instructions.';
    return '<p class="clog-hint">' + base + days + '</p>';
  }


  function paintShowIf() {
    var sel = document.querySelectorAll('[data-k="implant"]');
    Array.prototype.forEach.call(sel, function (s) {
      var row = s.getAttribute('data-row');
      var t = document.querySelector('[data-row="' + row + '"][data-k="implantLot"]');
      if (!t) { return; }
      var wrap = t.closest('.clog-field');
      if (wrap) { wrap.style.display = (s.value === 'yes') ? '' : 'none'; }
    });
  }

  function updateCounts() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-count]'), function (n) {
      var k = n.getAttribute('data-count');
      var v = (S[k] || []).length;
      if (k !== 'loads') { n.textContent = v ? '(' + v + ')' : ''; }
      else {
        var total = 0;
        S.loads.forEach(function (r) { total += 1; });
        n.textContent = total ? '(' + total + ' official field sets: ' + (total * 20) + ' data points)' : '';
      }
    });
    refreshDatalists();
  }

  function refreshDatalists() {
    var st = {}, sz = {};
    S.loads.forEach(function (r) {
      if (r.ohcw) { st[r.ohcw] = 1; }
      if (r.sterilizer) { sz[r.sterilizer] = 1; }
    });
    var a = Object.keys(st).map(function (x) { return '<option value="' + esc(x) + '">'; }).join('');
    var b = Object.keys(sz).map(function (x) { return '<option value="' + esc(x) + '">'; }).join('');
    var ea = document.getElementById('clog-staff'), eb = document.getElementById('clog-sterilizers');
    if (ea) { ea.innerHTML = a; }
    if (eb) { eb.innerHTML = b; }
  }

  /* ---------- carry forward, sample preview, demand signals (v1.0.6) ---------- */

  /* P0-1: carry only CARRY_FIELDS forward from the row above. Everything else stays empty on
     purpose: the load number must never repeat, and CI/BI/PCD/Bowie-Dick results are per-load
     judgements. Returns how many values were carried. */
  function carryForward(idx) {
    if (idx < 1) { return 0; }
    var prev = S.loads[idx - 1], cur = S.loads[idx];
    if (!prev || !cur) { return 0; }
    var n = 0;
    CARRY_FIELDS.forEach(function (k) {
      var v = prev[k];
      if (v != null && String(v).length) { cur[k] = v; n++; }
    });
    if (!cur.date) { cur.date = TODAY; }
    /* Provenance marker. It lives in state but is NOT in the field spec, so the CSV writer and
       the printed sheet (both driven by allFields()) can never emit it.
       Only mark when something was ACTUALLY carried: claiming "values carried forward" on an
       empty source row tells the user data came across when nothing did. */
    if (n > 0) { cur.carriedFrom = idx; delete cur.carryEmpty; }
    else { delete cur.carriedFrom; cur.carryEmpty = idx; }
    return n;
  }

  /* P0-2: a read-only example. Values are obviously fictional, live only in this function, and
     are never written into S, so they cannot be exported, printed or mistaken for a record. */
  function demoRows() {
    var d = TODAY;
    return {
      loads: [{ id: 'demo1', date: d, sterilizer: 'AUTOCLAVE-1 (example)', loadNo: 'DEMO-0001',
        contents: 'Instrument pouch + handpiece (example)', cycle: 'pre-vacuum (example)', time: '4',
        temp: '134', pressure: '212', ohcw: 'A. Example (example)', ci: 'pass', bi: 'pass',
        biCtrl: 'pass', pcd: 'n/a', bowie: 'pass', verifiedBy: 'A. Example (example)',
        lot: 'LOT-EXAMPLE-01', implant: 'no' }],
      service: [{ id: 'demo2', sDate: d, sProvider: 'Example Service Ltd.',
        sDone: 'Annual pressure-vessel service (example)' }],
      malfunctions: [{ id: 'demo3', mDate: d, mDesc: 'Door gasket worn (example)',
        mAction: 'Gasket replaced, Bowie-Dick test re-run (example)' }],
      labels: [{ id: 'demo4', lDate: d, lSterilizer: 'AUTOCLAVE-1 (example)', lLoadNo: 'DEMO-0001',
        lContents: 'See load record (example)', lInitials: 'AE' }],
      cleaning: [{ id: 'demo5', cDate: d, cEquip: 'Ultrasonic cleaner', cUnit: 'UC-1 (example)',
        cMethod: 'Foil test', cResult: 'pass', cSol: 'Changed 2026-09-23 / LOT-EXAMPLE (example)',
        cPm: 'Drain filter rinsed (example)', cBy: 'A. Example (example)' }]
    };
  }

  function demoTable(rows, gids) {
    var h = '';
    rows.forEach(function (r) {
      gids.forEach(function (gid) {
        var g = G.filter(function (x) { return x.id === gid; })[0];
        if (!g) { return; }
        var body = '';
        g.fields.forEach(function (f) {
          var v = r[f.k];
          if (v == null || String(v).length === 0) { return; }
          body += '<tr><th scope="row">' + esc(f.label) + '</th><td>' + esc(v) +
            (f.unit ? ' <span class="clog-unit">' + esc(f.unit) + '</span>' : '') + '</td></tr>';
        });
        if (body) { h += '<table class="clog-demo"><caption>' + esc(g.title) + '</caption><tbody>' + body + '</tbody></table>'; }
      });
    });
    return h;
  }

  function demoHtml() {
    var D = demoRows();
    return '<div class="clog-demo-inner">' +
      '<p class="clog-demo-banner"><strong>Example only - this is not a record.</strong> ' +
      'Every value below is made up. Nothing here is saved, exported or printable.</p>' +
      demoTable(D.loads, ['G1', 'G5', 'G6']) +
      demoTable(D.service, ['G2']) +
      demoTable(D.malfunctions, ['G3']) +
      demoTable(D.labels, ['G4']) +
      '<button type="button" class="clog-btn clog-primary" data-act="demo-close">Close example</button>' +
      '</div>';
  }

  function setExportDisabled(on) {
    var host = document.getElementById(MOUNT);
    if (!host) { return; }
    Array.prototype.forEach.call(
      host.querySelectorAll('button[data-act="csv"],button[data-act="print"]'),
      function (b) {
        b.disabled = !!on;
        if (on) { b.setAttribute('aria-disabled', 'true'); } else { b.removeAttribute('aria-disabled'); }
      });
  }

  function openDemo() {
    var host = document.getElementById(MOUNT);
    if (!host) { return; }
    var ov = document.getElementById('clog-demo-overlay');
    if (!ov) {
      ov = document.createElement('div');
      ov.id = 'clog-demo-overlay';
      ov.className = 'clog-overlay';
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');
      ov.setAttribute('aria-label', 'Example sterilization log sheet');
      host.appendChild(ov);
    }
    ov.innerHTML = demoHtml();
    ov.style.display = 'block';
    /* While an example is on screen the record controls are locked: an example must never
       reach a file or a printer. Same reason the printed sheet carries no demo rows. */
    setExportDisabled(true);
    var c = ov.querySelector('button[data-act="demo-close"]');
    if (c) { try { c.focus(); } catch (e) { /* focus is best-effort */ } }
  }

  function closeDemo() {
    var ov = document.getElementById('clog-demo-overlay');
    if (ov) { ov.style.display = 'none'; ov.innerHTML = ''; }
    setExportDisabled(false);
  }

  /* Demand signals. One flag per browser per signal: we are counting how many different people
     want the feature, not button presses. The flag is written before the event fires so a
     blocked gtag cannot turn one person into a repeat count. */
  function fireSignal(key, eventName) {
    try {
      if (window.localStorage.getItem(SIGNAL_KEYS[key])) { return false; }
      window.localStorage.setItem(SIGNAL_KEYS[key], '1');
    } catch (e) { /* private mode: still let the click expand, just skip the signal */ }
    track(eventName);
    return true;
  }

  /* ---------- events ---------- */
  function blank(kind) {
    var o = { id: uid(), touched: false };
    if (kind === 'loads') { o.date = TODAY; }
    else if (kind === 'service') { o.sDate = TODAY; }
    else if (kind === 'malfunctions') { o.mDate = TODAY; }
    else if (kind === 'labels') { o.lDate = TODAY; }
    else if (kind === 'cleaning') { o.cDate = TODAY; }
    return o;
  }

  function onInput(e) {
    var t = e.target;
    if (!t || !t.getAttribute) { return; }
    var rowId = t.getAttribute('data-row'), k = t.getAttribute('data-k');
    if (!rowId || !k) { return; }
    ['loads', 'service', 'malfunctions', 'labels', 'cleaning'].forEach(function (kind) {
      S[kind].forEach(function (r) { if (r.id === rowId) { r[k] = t.value; r.touched = true; } });
    });
    if (k === 'implant') { paintShowIf(); }
    if (k === 'ohcw' || k === 'sterilizer') { refreshDatalists(); }
    /* A stale "nothing was carried" note would be wrong the moment the user starts typing.
       onInput deliberately does NOT re-render (that would drop the caret), so the state flag and
       its DOM node are both cleared here by hand - otherwise the note lingers until the next
       render, which only happens on the next click. */
    if (CARRY_FIELDS.indexOf(k) !== -1) {
      var stale = false;
      S.loads.forEach(function (r) { if (r.carryEmpty != null) { delete r.carryEmpty; stale = true; } });
      if (stale) {
        var hostEl = document.getElementById(MOUNT);
        if (hostEl) {
          var staleNotes = hostEl.querySelectorAll('.clog-carry-empty');
          for (var si = 0; si < staleNotes.length; si++) {
            if (staleNotes[si].parentNode) { staleNotes[si].parentNode.removeChild(staleNotes[si]); }
          }
        }
      }
    }
    save();
  }

  function onClick(e) {
    var b = e.target.closest ? e.target.closest('button[data-act]') : null;
    if (!b) { return; }
    var act = b.getAttribute('data-act');
    if (act === 'add-load') { S.loads.push(blank('loads')); }
    else if (act === 'add-service') { S.service.push(blank('service')); }
    else if (act === 'add-malfunctions') { S.malfunctions.push(blank('malfunctions')); }
    else if (act === 'add-labels') { S.labels.push(blank('labels')); }
    else if (act === 'add-cleaning') { S.cleaning.push(blank('cleaning')); }
    else if (act === 'del') {
      var id = b.getAttribute('data-row'), kind = b.getAttribute('data-kind') || 'loads';
      S[kind] = S[kind].filter(function (r) { return r.id !== id; });
    } else if (act === 'copy-prev') {
      var rid = b.getAttribute('data-row'), idx = -1;
      S.loads.forEach(function (r, i) { if (r.id === rid) { idx = i; } });
      if (idx < 1) { return; }
      carryForward(idx);
    } else if (act === 'blank') { blankSheet(); return; }
    else if (act === 'demo') { openDemo(); return; }
    else if (act === 'demo-close') { closeDemo(); return; }
    else if (act === 'ics-daily') { downloadICS('daily'); return; }
    else if (act === 'ics-monthly') { downloadICS('monthly'); return; }
    else if (act === 'sig-imp' || act === 'sig-pro') {
      var isPro = (act === 'sig-pro');
      fireSignal(isPro ? 'pro' : 'imp', isPro ? 'tool_pro_interest' : 'tool_import_interest');
      var note = document.getElementById('clog-signal-note');
      if (note) {
        note.textContent = isPro
          ? 'Noted - this tells us there is demand. When a multi-client version exists, we will publish it here.'
          : 'Noted - this tells us there is demand. If importing an existing log becomes possible, we will publish it here.';
      }
      b.disabled = true;
      b.textContent = 'Noted';
      /* No re-render: nothing in the record changed, and the note must stay on screen. */
      return;
    } else if (act === 'print') { printDoc(); return; }
    else if (act === 'csv') { csv(); return; }
    else if (act === 'wipe') {
      if (!window.confirm('Clear every record stored in this browser? This cannot be undone.')) { return; }
      S.loads = []; S.service = []; S.malfunctions = []; S.labels = []; S.cleaning = [];
    } else { return; }
    save(); render();
  }

  /* ---------- exports ---------- */
  function allFields() {
    var out = [];
    G.forEach(function (g) { g.fields.forEach(function (f) { out.push({ g: g, f: f }); }); });
    return out;
  }

  function csvCell(v) { return '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"'; }

  function csv() {
    var lines = [], realRows = 0;
    var head = ['Record type', 'Row'];
    allFields().forEach(function (x) { head.push(x.f.label + (x.f.unit ? ' (' + x.f.unit + ')' : '') + (x.g.official ? '' : ' [' + (x.g.id === 'G5' ? 'extension' : 'additional') + ']')); });
    lines.push(head.map(csvCell).join(','));
    function push(kind, label) {
      (S[kind] || []).forEach(function (r, i) {
        if (!hasData(r)) { return; }   /* skip placeholder rows that only carry the auto-filled date */
        realRows++;
        var row = [label, i + 1];
        allFields().forEach(function (x) { row.push(r[x.f.k] == null ? '' : r[x.f.k]); });
        lines.push(row.map(csvCell).join(','));
      });
    }
    push('loads', 'Load record');
    push('service', 'Service & maintenance');
    push('malfunctions', 'Malfunction episode');
    push('labels', 'Package label');
    push('cleaning', 'Cleaning equipment test');
    lines.push(['# Fields 1-20 follow the RCDSO IPAC Self Audit Review Form (v2), p.8-p.9. Fields marked [extension]/[additional] are not part of that form.'].map(csvCell).join(','));
    lines.push(['# Free for clinics: 5-pack of 24-hour biological indicators \u2014 apply at clinieco.ca/pages/bi-sample-request (postage covered); buy without approval at clinieco.ca/products/clinieco-biological-indicator-5-pack-trial']);
    lines.push(['# Exported ' + localStamp() + ' · CliniEco Medical compliance log tool ' + VERSION + ' · ' + retentionPlain()].map(csvCell).join(','));
    var _jl = jurisPlainLine();
    if (_jl) { lines.push(['# ' + _jl].map(csvCell).join(',')); }
    var blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sterilization-log-' + localDate() + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    /* count-only telemetry: was there a real record, and how many load rows (never the content) */
    if (realRows === 0) { track('tool_export_empty'); }
    else { track('tool_export_csv', { loads: (S.loads || []).length }); }
  }

  function esc2(s) { return esc(s); }

  var PRINT_CSS = '<style>@page{size:letter;margin:14mm}body{font:11pt/1.4 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#000}' +
      'h1{font-size:15pt;margin:0 0 2mm}h2{font-size:12pt;margin:6mm 0 2mm;border-bottom:1pt solid #000;padding-bottom:1mm}' +
      'table{border-collapse:collapse;width:100%;font-size:10pt}th,td{border:0.5pt solid #444;padding:1.5mm;text-align:left;vertical-align:top;overflow-wrap:anywhere;word-break:break-word}' +
      '@media screen{table{table-layout:fixed;font-size:9pt}.sheet-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch}.note,p,li{overflow-wrap:anywhere}}' +
      'th{font-weight:600}.mono{font-family:ui-monospace,Menlo,Consolas,monospace}' +
      '.clog-pdf-hint{display:none}@media screen{.clog-pdf-hint{display:block}}' +
      '.note{font-size:10pt;margin-top:4mm}.pb{page-break-before:always}tr{page-break-inside:avoid}' + '.clog-banner{display:flex;justify-content:space-between;gap:6mm;border-bottom:1pt solid #000;padding-bottom:2mm;margin:0 0 3mm;font-size:9pt;line-height:1.35}.clog-banner-l strong{font-size:11.5pt}.clog-banner-r{text-align:right;max-width:52%}' +
      '</style>.clog-print-cta{margin:18px 0 0;padding:12px 14px;border:1px solid #bbb;border-radius:8px;font-size:11.5px;line-height:1.5;page-break-inside:avoid;}.clog-print-cta p{margin:0 0 6px;}.clog-print-cta p:last-child{margin-bottom:0;}.clog-print-cta strong{font-weight:700;} ';

  function printDoc() {
    var w = window.open('', '_blank');
    if (!w) { window.alert('Please allow pop-ups for this site to print the log.'); return; }
    var h = '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Sterilization log &mdash; ' + localDate() + '</title>' + PRINT_CSS + '</head><body>' + '<div class="clog-banner"><div class="clog-banner-l"><strong>CliniEco Medical</strong><br>Your around-the-clock sterilization supply partner \u00b7 clinieco.ca</div><div class="clog-banner-r">Health Canada Medical Device Establishment Licence (MDEL) #35334</div></div>' + saveAsPdfHintHtml();
    /* Unique record id: lets a printed sheet be traced back to a generation event.
       (No approval/signature block on purpose — that belongs to the user's own QMS.) */
    var recId = 'CLC-' + localDate().replace(/-/g, '') + '-' +
      (function () { var s2 = 0, t = JSON.stringify(S); for (var i = 0; i < t.length; i++) { s2 = (s2 * 31 + t.charCodeAt(i)) >>> 0; }
        return ('000000' + s2.toString(16)).slice(-6); })();
    h += '<h1>Sterilization &amp; compliance log</h1>' +
      '<p class="note"><strong>Record ID ' + esc2(recId) + '</strong> · generated ' + esc2(localStamp()) +
      ' · CliniEco Medical · ' + esc2(VERSION) + ' · ' + esc2(retentionPlain()) + '</p>';
    h += jurisPrintLine();

    function table(kind, gid, title) {
      var g = G.filter(function (x) { return x.id === gid; })[0];
      var rows = S[kind] || [], shown = 0;
      var body = '';
      rows.forEach(function (r, i) {
        if (!hasData(r, g.fields.map(function (f) { return { g: g, f: f }; }))) { return; }
        shown++;
        body += '<tr><td>' + (i + 1) + '</td>';
        g.fields.forEach(function (f) { body += '<td>' + esc2(r[f.k]) + '</td>'; });
        body += '</tr>';
      });
      if (!rows.length) { body = ''; }
      SEC_NO++;
      h += '<h2>' + SEC_NO + ' / ' + SEC_TOTAL + ' · ' + esc2(title) + '</h2><div class="sheet-wrap"><table><thead><tr><th>#</th>';
      g.fields.forEach(function (f) { h += '<th>' + esc2(f.label) + (f.unit ? ' (' + esc2(f.unit) + ')' : '') + '</th>'; });
      h += '</tr></thead><tbody>' + (body || '<tr><td colspan="' + (g.fields.length + 1) + '">&nbsp;</td></tr>') + '</tbody></table></div>';
      if (shown) { h += '<p class="note">' + shown + ' record(s).</p>'; }
    }

    var SEC_NO = 0, SEC_TOTAL = 7;   /* 7 tables are always emitted (G1-G7); bump if tables are added/removed */
    table('loads', 'G1', 'Load log — official fields, RCDSO 5542 p.9');
    table('loads', 'G5', 'Monitoring results (extension — not part of the official form)');
    table('loads', 'G6', 'Traceability (additional — not part of the official form)');
    table('service', 'G2', 'Service & maintenance log — RCDSO 5542 p.9');
    h += '<div class="pb"></div>';
    table('malfunctions', 'G3', 'Malfunction episodes — RCDSO 5542 p.9');
    table('labels', 'G4', 'Package labels — RCDSO 5542 p.8');
    table('cleaning', 'G7', 'Cleaning equipment testing & maintenance (additional — PHO IPAC Checklist 1.2)');
    h += '<p class="note">Field names and group order follow the RCDSO IPAC Self Audit Review Form (v2), p.8–p.9. ' +
      'Groups labelled “extension” / “additional” are provided by CliniEco Medical for traceability and indicator recordkeeping and are not part of the official form. ' +
      'For sterilizers without a recording device, physical parameters must be checked during the sterilization cycle for each load and documented. ' +
      'This sheet is an operational log generated by a browser-based tool; it is not a controlled-record original — '
      + 'copy it into your own document-control system if your QMS requires a controlled record. All ' + SEC_TOTAL +
      ' sections above must be present. ' +
      'This tool is for reference only and does not constitute compliance advice.</p>';
    /* Provenance line - GEO #3269 final (candidate a: brand+URL+tool+date, no MDEL).
       v1.0.13 (GEO #3381 P0-1): the province sentence used to be hard-coded here, which put
       Ontario's rule on the sheet of every visitor including the ones who had just switched to
       another province. The provincially correct statement is emitted by jurisPrintLine() above,
       which is the only place that states one. Added to BOTH print exits. */
    h += '<p class="note"><strong>CliniEco Medical &middot; clinieco.ca</strong> &middot; Medical Device Establishment Licence (MDEL) #35334</p>' +
      '<p class="note">Prepared with the free CliniEco sterilization record tool &mdash; clinieco.ca<br>' +
      'Generated ' + esc2(localDate()) + '.</p>';
    h += '<div class="clog-print-cta">'+
      '<p><strong>Free for clinics: a 5-pack of 24-hour biological indicators.</strong> '+
      'Your log sheet is half the record \u2014 the other half is spore testing. '+
      'Clinics in Canada can apply for a free 5-pack (postage covered): '+
      '<br><strong>clinieco.ca/pages/bi-sample-request</strong></p>'+
      '<p>No application needed: the same 5-pack, CAD 12.99, ships without approval \u2014 '+
      '<strong>clinieco.ca/products/clinieco-biological-indicator-5-pack-trial</strong></p>'+
      '<p>Questions on frequency, retention or inspections: '+
      '<strong>clinieco.ca/pages/sterilization-compliance-consultation</strong></p>'+
      '</div>';
    h += '</body></html>';
    w.document.open(); w.document.write(h); w.document.close();
    track('tool_print', { loads: (S.loads || []).length });
    w.focus();
    try { w.print(); } catch (e) { /* user can print from the opened window */ }
  }

  /* ---------- jurisdiction line (v1.0.8 - benchmark P0-2 / P0-3) ----------
     Two rules:
       P0-2  the visitor states their province before we claim anything about them;
       P0-3  whatever requirement we state is printed on the sheet itself, with its source.
     Only Ontario has been checked line by line against its own regulator's published text.
     Everywhere else we say so plainly rather than print a number: a rule someone relies on
     that turns out to be wrong is worse than no rule at all. Same reasoning as the sector
     switcher above - the reader should be able to tell verified from not-yet-verified. */
  var JURIS_DEFAULT = 'ON';
  var JURIS_VERIFIED = 'ON';
  var JURIS = [
    ['ON', 'Ontario'], ['QC', 'Quebec'], ['BC', 'British Columbia'], ['AB', 'Alberta'],
    ['MB', 'Manitoba'], ['SK', 'Saskatchewan'], ['NS', 'Nova Scotia'], ['NB', 'New Brunswick'],
    ['NL', 'Newfoundland and Labrador'], ['PE', 'Prince Edward Island'],
    ['YT', 'Yukon'], ['NT', 'Northwest Territories'], ['NU', 'Nunavut']
  ];
  var ON_DENTAL_REQ = 'Biological indicator (BI) plus a control, once daily for each type of cycle ' +
    '(RCDSO IPAC Self Audit Review Form v2). Keep the records at least 10 years ' +
    '(RCDSO IPAC Standard of Practice v3; the same form: "Logbook kept for 10 years after last entry").';
  var UNVERIFIED_TXT = 'We have not checked this province or territory line by line against its own ' +
    "regulator's published text, so we do not print a requirement for it - check your own regulator. " +
    'The field set does not change by province: it is the RCDSO form fields in every case.';

  /* ---------- citation discipline (GEO #3381 P1 · Hu 2026-09-13) ----------
     A statement about a regulator's rule is only checkable if it names the body, the document,
     the clause, when it was last checked and where to read it. The page body carries the full
     reference list; these two forms let the generated artefacts carry the same provenance, at
     two lengths: the printed sheet and the CSV get the direct source links, the .ics gets the
     short form because it repeats in a calendar strip and is a prompt, not a record. */
  var JURIS_CHECKED = '2026-09-13';
  var SRC_5539 = 'https://cdn.agilitycms.com/rcdso/pdf/ipac/' +
    'RCDSO_5539_Standard%20of%20Practice_Infection%20Prevention%20and%20Control_v3_ACC.pdf';
  var SRC_5542 = 'https://cdn.agilitycms.com/rcdso/pdf/ipac/' +
    'RCDSO_5542_IPAC%20Self%20Audit%20Review%20Form_v2_RD_ACC.pdf';
  function sourcesShort() {
    return 'Sources: RCDSO 5539 v3 p.24, RCDSO 5542 v2 p.8-p.9 - last checked ' + JURIS_CHECKED +
      ', reference list with links at https://clinieco.ca/pages/compliance-log-center.';
  }
  function sourcesFull() {
    return 'Sources, last checked ' + JURIS_CHECKED + ': RCDSO IPAC Standard of Practice v3, p.24 - ' +
      SRC_5539 + ' | RCDSO IPAC Self Audit Review Form v2, p.8-p.9 - ' + SRC_5542 + '.';
  }

  /* ---------- setting-dependent requirement text (v1.0.10 · GEO #3114) ----------
     The retention period and the BI cadence stated in this tool are RCDSO requirements for
     Ontario DENTAL practice - that is what we have read in the regulator's own published text.
     Every other sector has its own rule and we have not verified those, so we do not print a
     figure for them: a rule someone relies on that turns out to be wrong is worse than no rule
     at all. Same reasoning as jurisPlainLine() above - deliberately one set of criteria. */
  function isDental() { return currentSetting() === 'dental'; }
  function retentionPlain() {
    /* GEO #3381 P0-1: this line is printed in the record header and written into the CSV, so it
       is gated on the province as well as the sector - otherwise a switched visitor still reads
       Ontario's 10-year rule, just without the word "Ontario" in it. */
    return (isDental() && currentJuris() === JURIS_VERIFIED)
      ? 'Logbooks must be kept for at least 10 years from the date of the last entry.'
      : "Retention period: check your sector's own framework - we have not verified a figure for it, so we do not print one.";
  }
  function biHintHtml() {
    if (isDental() && currentJuris() !== JURIS_VERIFIED) {
      return '<p class="clog-hint">Biological indicator frequency: no interval is stated for ' +
        esc2(jurisName(currentJuris())) + ' - we have not verified that province or territory against ' +
        "its own regulator's published text, so we do not print one for it.</p>";
    }
    return '<p class="clog-hint">' + (isDental()
      ? 'Biological indicator + control: once daily for each type of cycle (RCDSO IPAC Standard of Practice v3, p.24). Weekly is the U.S. CDC/AAMI baseline, not the Ontario requirement. ' + sourcesShort()
      : "Biological indicator frequency: check your sector's own framework - we have not verified the interval for it, so we do not print one.") + '</p>' + biSupplyHtml();
  }

  /* ---------- BI 供货线（2026-10-05 收件箱 #6063 字段级焊点）----------
     只写「我们备着这个 + 价格 + 从多伦多发货」三件事实：
     不写推荐/最佳/你应该，不写任何库存量或销量，不下合规结论。
     价格必须与商品页同源（落刀前已对拍：CA $59.99 = 25-pack）。 */
  function biSupplyHtml() {
    return '<p class="clog-hint"><strong>No BI on hand for this cycle?</strong> '
      + 'We stock the <a href="/products/clinieco-medical-biological-indicator-24h-rapid-readout-25-pack">'
      + '24-hour readout 25-pack &mdash; CA $59.99</a>, shipped from our Toronto warehouse.</p>';
  }

  function isJuris(v) {
    for (var i = 0; i < JURIS.length; i++) { if (JURIS[i][0] === v) { return true; } }
    return false;
  }
  function jurisName(v) {
    for (var i = 0; i < JURIS.length; i++) { if (JURIS[i][0] === v) { return JURIS[i][1]; } }
    return v;
  }
  function currentSetting() {
    /* GEO #5825 改3（2026-10-05）：未选场景时返回空串，不再回落成 dental。
       所有消费者都只做 === 'dental' 比较，空串会正确地走"未核实/中性"分支。 */
    var b = document.getElementById('clog-setting');
    return (b && b.getAttribute('data-setting')) || '';
  }
  function currentJuris() {
    var s = document.getElementById('clog-juris');
    var v = (s && s.value) || JURIS_DEFAULT;
    return isJuris(v) ? v : JURIS_DEFAULT;
  }
  /* ---------- ONE jurisdiction statement, three carriers (GEO #3381 P0-1) ----------
     Before v1.0.13 the requirement reached the visitor through three separate code paths and only
     the on-screen panel followed the province switcher: the printed provenance line had its own
     hard-coded Ontario sentence, the CSV footer used retentionPlain(), and the .ics built a third
     sentence. A Quebec or British Columbia visitor could therefore be handed an Ontario rule on
     paper and in the file even though the panel above said "not yet verified for this choice".
     Every carrier now asks this one function; nothing else in this file states a jurisdiction
     rule. mode 'short' = .ics (repeats daily in a calendar), default = sheet + CSV. */
  function jurisPlainLine(mode) {
    if (currentSetting() !== 'dental') { return ''; }
    var v = currentJuris();
    if (v !== JURIS_VERIFIED) { return jurisName(v) + ': ' + UNVERIFIED_TXT; }
    return jurisName(v) + ': ' + ON_DENTAL_REQ + ' ' +
      (mode === 'short' ? sourcesShort() : sourcesFull());
  }
  /* P0-3: printed on the sheet itself, next to the Record ID. The province name is inside the
     statement now, so it is not prefixed a second time. */
  function jurisPrintLine() {
    var t = jurisPlainLine();
    if (!t) { return ''; }
    return '<p class="note">' + esc2(t) + '</p>';
  }
  function renderJuris() {
    var nm = document.getElementById('clog-juris-name');
    var req = document.getElementById('clog-setting-req');
    var flag = document.getElementById('clog-juris-flag');
    var cnt = document.getElementById('clog-juris-count');
    var v = currentJuris(), dental = (currentSetting() === 'dental');
    if (nm) { nm.textContent = jurisName(v); }
    var verified = (dental && v === JURIS_VERIFIED);
    if (flag) {
      flag.textContent = verified ? "Verified against the regulator's published text"
                                  : 'Not yet verified for this choice';
      flag.className = 'clog-setting-flag ' + (verified ? 'clog-ok' : 'clog-todo');
    }
    if (req) {
      req.innerHTML = !currentSetting()
        /* GEO #5825 改3 的收尾：未选场景时不要提牙科，否则和上面的 "not selected yet" 自相矛盾。 */
        ? "Pick your setting above. We state a requirement only where we have verified it against the regulator\u2019s own published text."
        : (!dental
          ? "The requirement line applies to dental practice in Ontario. Your sector's framework is the one shown above."
          : esc2(v === JURIS_VERIFIED ? ON_DENTAL_REQ + ' ' + sourcesShort() : UNVERIFIED_TXT));
    }
    if (cnt) { cnt.textContent = '1 of ' + JURIS.length + ' jurisdictions checked against its own regulator, so far.'; }
    syncRemLead();   /* GEO #3324: the reminder line follows the jurisdiction switcher too */
  }
  function initJuris() {
    var sel = document.getElementById('clog-juris');
    if (!sel) { return; }
    var want = '';
    try { want = (new URLSearchParams(location.search)).get('province') || ''; } catch (e) { want = ''; }
    want = String(want || sel.value || JURIS_DEFAULT).toUpperCase();
    if (!isJuris(want)) { want = JURIS_DEFAULT; }
    sel.value = want;
    sel.hidden = false;
    sel.addEventListener('change', renderJuris);
    renderJuris();
  }

  /* ---------- P0-1: one-click deliverables (v1.0.8) ----------
     The visitor should be able to walk away with something useful without typing a single field.
     Typing the fields is still there - it is just no longer the price of entry. */
  function initHero() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t.getAttribute) {
        var a = t.getAttribute('data-clog-act');
        if (a) {
          if (e.preventDefault) { e.preventDefault(); }
          if (a === 'blank') { blankSheet(); }
          else if (a === 'demo') { showDemo(); }
          return;
        }
        t = t.parentNode;
      }
    });
  }
  function showDemo() {
    var host = document.getElementById(MOUNT);
    if (host && host.scrollIntoView) {
      try { host.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { host.scrollIntoView(); }
    }
    try { openDemo(); } catch (e2) { /* if the tool is not up yet, nothing to show */ }
  }

  /* A blank sheet is a template, not a record: no Record ID, no record count, said out loud on
     the sheet. It must never look like evidence of something that happened. */
  function blankSheet() {
    var w = window.open('', '_blank');
    if (!w) { window.alert('Please allow pop-ups for this site to print the sheet.'); return; }
    var SEC_T = 7, SEC_N = 0;
    var h = '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Sterilization log - blank sheet - ' + localDate() + '</title>' + PRINT_CSS + '</head><body>' + '<div class="clog-banner"><div class="clog-banner-l"><strong>CliniEco Medical</strong><br>Your around-the-clock sterilization supply partner \u00b7 clinieco.ca</div><div class="clog-banner-r">Health Canada Medical Device Establishment Licence (MDEL) #35334</div></div>' + saveAsPdfHintHtml();
    h += '<h1>Sterilization &amp; compliance log &mdash; blank sheet</h1>' +
      '<p class="note"><strong>BLANK TEMPLATE &mdash; nothing is recorded on this sheet.</strong> generated ' +
      esc2(localStamp()) + ' &middot; CliniEco Medical &middot; ' + esc2(VERSION) + '</p>' + jurisPrintLine() +
      '<p class="note">Print it and fill it in by hand. To get a filled sheet carrying a Record ID, enter your loads in the tool on this page.</p>';
    function bt(gid, title) {
      var g = G.filter(function (x) { return x.id === gid; })[0];
      SEC_N++;
      h += '<h2>' + SEC_N + ' / ' + SEC_T + ' &middot; ' + esc2(title) + '</h2><div class="sheet-wrap"><table><thead><tr><th>#</th>';
      g.fields.forEach(function (f) { h += '<th>' + esc2(f.label) + (f.unit ? ' (' + esc2(f.unit) + ')' : '') + '</th>'; });
      h += '</tr></thead><tbody>';
      for (var r = 0; r < 6; r++) {
        h += '<tr><td>' + (r + 1) + '</td>';
        for (var c = 0; c < g.fields.length; c++) { h += '<td>&nbsp;</td>'; }
        h += '</tr>';
      }
      h += '</tbody></table></div>';
    }
    bt('G1', 'Load log &mdash; official fields, RCDSO 5542 p.9');
    bt('G5', 'Monitoring results (extension &mdash; not part of the official form)');
    bt('G6', 'Traceability (additional &mdash; not part of the official form)');
    bt('G2', 'Service &amp; maintenance log &mdash; RCDSO 5542 p.9');
    h += '<div class="pb"></div>';
    bt('G3', 'Malfunction episodes &mdash; RCDSO 5542 p.9');
    bt('G4', 'Package labels &mdash; RCDSO 5542 p.8');
    bt('G7', 'Cleaning equipment testing &amp; maintenance &mdash; additional, PHO IPAC Checklist 1.2');
    h += '<p class="note">Field names and group order follow the RCDSO IPAC Self Audit Review Form (v2), p.8&ndash;p.9. ' +
      'Groups labelled &ldquo;extension&rdquo; / &ldquo;additional&rdquo; are provided by CliniEco Medical and are not part of the official form. ' +
      'This is a blank template, not a record: it carries no Record ID and no record count. All ' + SEC_T +
      ' sections must be present. This tool is for reference only and does not constitute compliance advice.</p>';
    /* Provenance line - GEO #3269 final (candidate a: brand+URL+tool+date, no MDEL).
       v1.0.13 (GEO #3381 P0-1): province sentence removed here too - jurisPrintLine() carries it,
       from the one generator, in the province the visitor actually selected. */
    h += '<p class="note"><strong>CliniEco Medical &middot; clinieco.ca</strong> &middot; Medical Device Establishment Licence (MDEL) #35334</p>' +
      '<p class="note">Prepared with the free CliniEco sterilization record tool &mdash; clinieco.ca<br>' +
      'Generated ' + esc2(localDate()) + '.</p>';
    h += '<div class="clog-print-cta">'+
      '<p><strong>Free for clinics: a 5-pack of 24-hour biological indicators.</strong> '+
      'Your log sheet is half the record \u2014 the other half is spore testing. '+
      'Clinics in Canada can apply for a free 5-pack (postage covered): '+
      '<br><strong>clinieco.ca/pages/bi-sample-request</strong></p>'+
      '<p>No application needed: the same 5-pack, CAD 12.99, ships without approval \u2014 '+
      '<strong>clinieco.ca/products/clinieco-biological-indicator-5-pack-trial</strong></p>'+
      '<p>Questions on frequency, retention or inspections: '+
      '<strong>clinieco.ca/pages/sterilization-compliance-consultation</strong></p>'+
      '</div>';
    h += '</body></html>';
    w.document.open(); w.document.write(h); w.document.close();
    try { track('tool_blank_sheet', { blank: 1 }); } catch (e3) { /* telemetry must never break print */ }
    w.focus();
    try { w.print(); } catch (e4) { /* user can print from the opened window */ }
  }

  /* ---------- setting switcher (v1.0.7 · GEO #2832 P0-1 / P0-2) ----------
     P0-1  a visitor only ever sees their own sector's framework. The other sectors are not in
           the document at all: their copy is built on demand, from this file, when asked for.
     P0-2  entry = identity. The page opens on Dental clinics, which is the sector our content
           and our outreach are written for. The visitor is never asked to pick a sector before
           using the tool - one lightweight switch, nothing more.

     The dental row is the server-rendered copy in the page body and is captured from the DOM at
     init, so the visible page stays the single source of truth for it: this file cannot drift
     from what a search engine or a no-JS visitor reads. */
  var SETTING_DEFAULT = 'dental';
  var SETTING_ORDER = ['dental', 'medical', 'ltc', 'lab', 'food'];
  var SETTINGS = {
    dental: { name: 'Dental clinics' },                       /* body captured from the page */
    medical: {
      name: 'Medical & family practice clinics',
      framework: 'Provincial IPAC requirements and public health unit IPAC checklists; requirements differ by province and by equipment.',
      fields: 'Same field set as every setting; the record groups follow the RCDSO IPAC Self Audit Review Form (v2) p.8\u2013p.9, which is the dental template we map to. For clinics your framework is provincial IPAC requirements and your public health unit\u2019s IPAC checklist.'
    },
    ltc: {
      name: 'Long-term care & retirement homes',
      framework: 'Provincial long-term care legislation and public health IPAC requirements.',
      fields: 'Same field set as every setting; the record groups follow the RCDSO IPAC Self Audit Review Form (v2) p.8\u2013p.9, which is the dental template we map to. For long-term care your framework is the Fixing Long-Term Care Act, 2021 and O. Reg. 246/22 (infection prevention and control program) with PHO IPAC guidance.'
    },
    lab: {
      name: 'Diagnostic & research laboratories',
      framework: 'ISO 15189 quality-management requirements for medical laboratories, plus specimen transport requirements.',
      fields: 'Same field set as every setting; the record groups follow the RCDSO IPAC Self Audit Review Form (v2) p.8\u2013p.9, which is the dental template we map to. For laboratories your framework is the ISO 15189 quality-management requirements for medical laboratories.'
    },
    food: {
      name: 'Food manufacturing & processing',
      framework: 'Safe Food for Canadians Regulations (SFCR), Part 4 \u2014 preventive controls, and the preventive control plan requirements published by the CFIA.',
      fields: 'Same field set as every setting; the record groups follow the RCDSO IPAC Self Audit Review Form (v2) p.8\u2013p.9, which is the dental template we map to. For food manufacturing your framework is the CFIA preventive control plan requirements under the Safe Food for Canadians Regulations, Part 4.'
    }
  };

  function setSetting(id, opts) {
    var box = document.getElementById('clog-setting');
    if (!box || !SETTINGS[id]) { return false; }
    var s = SETTINGS[id];
    box.setAttribute('data-setting', id);
    var n = document.getElementById('clog-setting-name');
    if (n) { n.textContent = s.name; }
    var f = document.getElementById('clog-setting-framework');
    if (f) { f.innerHTML = s.frameworkHtml || ('<strong>Record-keeping framework:</strong> ' + s.framework); }
    var m = document.getElementById('clog-setting-fields');
    if (m) { m.innerHTML = s.fieldsHtml || ('<strong>Fields mapped:</strong> ' + s.fields); }
    var list = document.getElementById('clog-setting-list');
    if (list) { list.hidden = true; list.innerHTML = ''; }
    var btn = document.getElementById('clog-setting-switch');
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML = 'Change setting \u25be';
    }
    var note = document.getElementById('clog-setting-note');
    if (note) { note.textContent = 'Showing ' + s.name + '.'; }
    try { renderJuris(); } catch (e1) { /* sector switching must never break on this */ }
    /* v1.0.10: the retention and BI lines depend on the setting, so anything already painted
       has to be rebuilt - otherwise switching sector on the page leaves the previous
       sector's requirement on screen until the next render. */
    try { if (document.querySelector('#' + MOUNT + ' .clog-head')) { render(); } } catch (e2) { }
    if (opts && opts.url === true && window.history && history.replaceState) {
      try {
        var u = new URL(location.href);
        u.searchParams.set('setting', id);
        history.replaceState(null, '', u.toString());
      } catch (e) { /* URL sync is a convenience, never a requirement */ }
    }
    return true;
  }

  function settingChoices(current) {
    var h = '<p class="clog-setting-ask">Which setting matches yours?</p>', i, id;
    for (i = 0; i < SETTING_ORDER.length; i++) {
      id = SETTING_ORDER[i];
      /* GEO #5825 C2（2026-10-05）：必须列出全部 5 个场景 —— 原代码把当前场景 continue 掉，
         验收器只数到 4 个。当前场景也做成按钮（再点一次=重选同一个，无副作用），并标 current。 */
      h += '<button type="button" class="clog-btn clog-quiet clog-setting-opt' +
        (id === current ? ' clog-setting-opt--current' : '') + '" data-setting-pick="' + id + '"' +
        (id === current ? ' aria-current="true"' : '') + '>' + esc(SETTINGS[id].name) +
        (id === current ? ' \u2713' : '') + '</button>';
    }
    return h;
  }

  function initSetting() {
    var box = document.getElementById('clog-setting');
    if (!box) { return; }
    var fEl = document.getElementById('clog-setting-framework');
    var mEl = document.getElementById('clog-setting-fields');
    /* page body is authoritative for the dental copy */
    if (fEl) { SETTINGS.dental.frameworkHtml = fEl.innerHTML; }
    if (mEl) { SETTINGS.dental.fieldsHtml = mEl.innerHTML; }

    var btn = document.getElementById('clog-setting-switch');
    if (btn) {
      btn.hidden = false;                       /* only offered once this module is live */
      btn.addEventListener('click', function () {
        var list = document.getElementById('clog-setting-list');
        if (!list) { return; }
        if (list.hidden === false) {
          list.hidden = true; list.innerHTML = ''; btn.setAttribute('aria-expanded', 'false');
          return;
        }
        list.innerHTML = settingChoices(box.getAttribute('data-setting') || SETTING_DEFAULT);
        list.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      });
    }

    box.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== box) {
        if (t.getAttribute && t.getAttribute('data-setting-pick')) {
          setSetting(t.getAttribute('data-setting-pick'), { url: true });
          syncRemLead();
          return;
        }
        t = t.parentNode;
      }
    });

    /* GEO #5825 改1（2026-10-05）：点页内「Choose your setting」⇒ 滚动照旧，
       同时【复用现成的展开逻辑】把选项打开。不 preventDefault、不自己改 hidden/display、
       不碰 aria-expanded —— 状态一致性全部交给 #clog-setting-switch 自己的 handler。
       面板是异步渲染的，所以用 document 事件委托，不用 DOMContentLoaded 直接绑。 */
    function openChoices() {
      var sw = document.getElementById('clog-setting-switch');
      var list = document.getElementById('clog-setting-list');
      if (sw && list && list.hidden) { sw.click(); }
    }
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== document) {
        if (t.tagName === 'A' && t.getAttribute && t.getAttribute('href') === '#clog-setting') {
          setTimeout(openChoices, 0);
          return;
        }
        t = t.parentNode;
      }
    }, true);
    window.clogOpenChoices = openChoices;

    var want = '';
    try { want = (new URLSearchParams(location.search)).get('setting') || ''; } catch (e) { want = ''; }
    want = String(want).toLowerCase();
    if (SETTINGS[want]) {
      setSetting(want, { url: false });
    } else {
      /* GEO #5825 改3（做法A）：没有指定场景时不预选牙科 —— 给中性未选态，并把选项摊开。
         页面文案说的是"works for five settings"，预选牙科与它直接冲突（胡总这次问题的起点）。 */
      box.setAttribute('data-setting', '');
      var sn = document.getElementById('clog-setting-name');
      if (sn) { sn.textContent = 'not selected yet \u2014 choose your sector above'; }
      var sf = document.getElementById('clog-setting-framework');
      if (sf) { sf.innerHTML = '<strong>Record-keeping framework:</strong> pick your setting above and we will show the framework that applies to it.'; }
      var sm2 = document.getElementById('clog-setting-fields');
      if (sm2) { sm2.innerHTML = ''; }
      var sr = document.getElementById('clog-setting-req');
      if (sr) { sr.textContent = 'No requirement is shown until you pick a setting. We state a requirement only where we have verified it against the regulator\u2019s own published text.'; }
      if (btn) { btn.hidden = false; btn.innerHTML = 'Change setting \u25be'; btn.setAttribute('aria-expanded', 'true'); }
      var sl = document.getElementById('clog-setting-list');
      if (sl) {
        sl.innerHTML = settingChoices('');
        sl.hidden = false;
      }
      try { renderJuris(); } catch (e3) { }
      try { if (document.querySelector('#' + MOUNT + ' .clog-head')) { render(); } } catch (e4) { }
    }
  }

  /* ---------- boot ---------- */
  function boot() {
    try { initSetting(); } catch (e) { /* a switcher problem must never stop the log tool */ }
    try { initJuris(); } catch (e) { }
    try { initHero(); } catch (e) { }
    var host = document.getElementById(MOUNT);
    if (!host) { return; }
    load();
    /* seed one empty row per group so every official field is visible on first paint */
    if (!S.loads.length) { S.loads.push(blank('loads')); }
    if (!S.service.length) { S.service.push(blank('service')); }
    if (!S.malfunctions.length) { S.malfunctions.push(blank('malfunctions')); }
    if (!S.labels.length) { S.labels.push(blank('labels')); }
    render();
    host.addEventListener('input', onInput);
    host.addEventListener('change', onInput);
    host.addEventListener('click', onClick);
    window.cliniecoComplianceLog = { csv: csv, print: printDoc, blank: blankSheet, demo: showDemo, state: S };
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();


/* =============================================================================
 * CliniEco compliance log tool — v1.1.0 one-tap PDF download (2026-09-28, Hu)
 *
 * WHY: on a phone the print dialogue defaults to a printer and there is no
 * "save as a file" step; the sheet's own footer carried the brand but the HEADER
 * carried nothing. This module adds a real download: a PDF generated in the
 * browser, whose header and footer are entirely ours.
 *
 * HOW IT STAYS IN SYNC WITH THE PRINTED SHEET: it does not re-implement the
 * field spec. It calls the existing printDoc()/blankSheet() with window.open
 * temporarily stubbed, captures the exact HTML they would have printed, parses
 * it, and lays the same tables out into the PDF. One source of truth for the
 * content; this file only owns the page furniture (header/footer) and the file.
 *
 * BRANDING (Hu 2026-09-28): header = CliniEco Medical + tagline + MDEL number,
 * footer = clinieco.ca + generation date + page n of N, on every page.
 * NOTE: MDEL #35334 is an ESTABLISHMENT licence (importer), not a device
 * approval, so it is printed as a factual licence statement and never as
 * "approved / licensed by Health Canada". GEO #3269 had deliberately kept the
 * MDEL off this sheet; Hu reversed that on 2026-09-28.
 *
 * v1.1.1 (2026-09-28, after Hu reported the header reading "CliniEco Medical"
 * as ghosted/overlapping). Two defects in the first build, both measured off the
 * PDF content stream (not read off the source):
 *  1) GEOMETRY. Content started at M+9 (21mm) while the header block is fixed
 *     geometry: brand baseline 17.5mm (11pt), tagline 22.5mm (8pt), rule 25.5mm.
 *     The 14pt title's capitals reach 3.56mm above its own baseline, i.e. to
 *     17.44mm - straight into the brand's baseline - and the tagline was then
 *     printed across the title's descenders. Three lines inside 5mm = the
 *     "double image". Content now starts at M+20 (32mm).
 *  2) REDUNDANCY. furniture() hung off autoTable's didDrawPage, which fires once
 *     PER TABLE SECTION; this sheet has 8 sections, so every header and footer
 *     line was painted 8x per page. Byte-identical, so it never showed as an
 *     offset ghost - but it bloated the file and left "Page 1 of " underneath
 *     "Page 1 of 1". furniture() is now painted exactly once per page by the
 *     final pass below.
 *
 * COST: jsPDF + autoTable are loaded LAZILY, only when the visitor asks for the
 * PDF, so the page's critical path is unchanged.
 * No data leaves the browser. Header/footer are static strings - no user data.
 * ========================================================================== */
(function () {
  'use strict';

  var VERSION = 'v1.1.0';
  var TAGLINE = 'Your around-the-clock sterilization supply partner';
  var MDEL = 'Health Canada Medical Device Establishment Licence (MDEL) #35334';
  var SITE = 'clinieco.ca';
  var LIB_DIR;   /* resolved from the tool's own <script> src */

  function libDir() {
    if (LIB_DIR) { return LIB_DIR; }
    var s = document.querySelector('script[src*="clinieco-compliance-tool"]');
    LIB_DIR = s && s.src ? s.src.replace(/clinieco-compliance-tool\.js.*$/, '') : '/cdn/shop/t/8/assets/';
    return LIB_DIR;
  }

  var libsLoaded = false;
  function loadLibs(cb) {
    if (libsLoaded || (window.jspdf && window.jspdf.jsPDF)) { libsLoaded = true; return cb(null); }
    var files = ['jspdf.umd.min.js', 'jspdf-autotable.umd.min.js'];
    var i = 0, failed = null;
    (function next() {
      if (i >= files.length) { libsLoaded = true; return cb(failed); }
      var s = document.createElement('script');
      s.src = libDir() + files[i++];
      s.onload = next;
      s.onerror = function () { failed = failed || s.src; next(); };
      document.head.appendChild(s);
    })();
  }

  function stamp() {
    var T = window.cliniecoComplianceLog;
    var d = new Date();
    function p(n) { return (n < 10 ? '0' : '') + n; }
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' +
           p(d.getHours()) + ':' + p(d.getMinutes());
  }
  function today() { return stamp().slice(0, 10); }

  /* Capture the HTML the base tool already builds for printing, without
     opening a window and without printing. */
  function captureHtml(which) {
    var T = window.cliniecoComplianceLog;
    if (!T) { return null; }
    var html = null, orig = window.open;
    window.open = function () {
      return {
        document: {
          open: function () {}, close: function () {},
          write: function (h) { html = h; }
        },
        focus: function () {}, print: function () {}
      };
    };
    try {
      if (which === 'blank' && typeof T.blank === 'function') { T.blank(); }
      else { T.print(); }
    } catch (e) {
      html = null;
    } finally {
      window.open = orig;
    }
    return html;
  }

  function buildPdf(html, opts) {
    var jsPDF = window.jspdf.jsPDF;
    var doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'portrait' });
    var PW = doc.internal.pageSize.getWidth();
    var PH = doc.internal.pageSize.getHeight();
    var M = 12;                       /* page margin mm */
    /* 2026-09-28 fix: content used to start at M+9 = 21mm, which collided with the
       brand/tagline block (tagline at 22.5mm) -> the title overlapped the tagline in
       the PDF Hu downloaded. Content now starts below the header rule (25.5mm). */
    var HEAD = M + 20;                /* first content baseline: below the header block */
    var FOOT = PH - 10;

    /* HTML -> plain text. <br> must become a separator: reading textContent directly
       joined "... tool - clinieco.ca" and "Generated ..." into "clinieco.caGenerated". */
    function txt(el) {
      if (!el) { return ''; }
      var c = el.cloneNode(true);
      var brs = c.querySelectorAll ? c.querySelectorAll('br') : [];
      for (var i = brs.length - 1; i >= 0; i--) {
        brs[i].parentNode.replaceChild(document.createTextNode(' \u00b7 '), brs[i]);
      }
      return (c.textContent || '').replace(/\s+/g, ' ').trim();
    }

    /* ---- page furniture: painted EXACTLY ONCE per page by the final pass at the
       end of buildPdf(). It must NOT also hang off autoTable's didDrawPage: that
       callback fires once per table section (8 on this sheet), so the header and
       footer would be repainted 8x per page - invisible, but it bloats the file
       and overprints "Page n of " under "Page n of N". v1.1.1 removed it. ---- */
    function furniture(page, total) {
      doc.setDrawColor(150);
      doc.setLineWidth(0.2);
      doc.line(M, M + 13.5, PW - M, M + 13.5);
      doc.line(M, FOOT - 3.5, PW - M, FOOT - 3.5);

      doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
      doc.text('CliniEco Medical', M, M + 5.5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(TAGLINE + ' \u00b7 ' + SITE, M, M + 10.5);

      doc.setFontSize(7.5);
      var mdelLines = doc.splitTextToSize(MDEL, (PW - 2 * M) * 0.52);
      doc.text(mdelLines, PW - M, M + 5.5, { align: 'right' });

      doc.setFontSize(7.5);
      doc.text(SITE + ' \u00b7 ' + MDEL.replace(/^Health Canada /, '') +
               ' \u00b7 generated ' + today() + ' \u00b7 Page ' + page + ' of ' + total,
               M, FOOT - 0.5);
    }

    /* ---- content parsed from the tool's own print document ---- */
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var h1 = parsed.querySelector('h1');
    var notes = [];
    parsed.querySelectorAll('body > p.note').forEach(function (p) {
      /* .clog-pdf-hint is the "how to save this from the print dialogue" instruction.
         It is screen-only by design (PRINT_CSS hides it on paper); since we re-render
         the text ourselves we must drop it explicitly - otherwise the PDF tells the
         reader how to save a PDF they already have. */
      if (p.classList && p.classList.contains('clog-pdf-hint')) { return; }
      if (notes.length < 4) { notes.push(txt(p)); }
    });

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
    doc.text(h1 ? txt(h1) : 'Sterilization & compliance log', M, HEAD);
    var y = HEAD + 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.6);
    notes.forEach(function (t) {
      doc.splitTextToSize(t, PW - 2 * M).forEach(function (l) { doc.text(l, M, y); y += 3.2; });
      y += 0.6;
    });

    var sections = parsed.querySelectorAll('h2');
    sections.forEach(function (h2) {
      var wrap = h2.nextElementSibling;
      var table = wrap && wrap.querySelector ? (wrap.tagName === 'TABLE' ? wrap : wrap.querySelector('table')) : null;
      if (!table) { return; }
      var head = [], body = [];
      table.querySelectorAll('thead tr').forEach(function (tr) {
        var r = [];
        tr.querySelectorAll('th').forEach(function (th) { r.push(txt(th)); });
        if (r.length) { head = [r]; }
      });
      table.querySelectorAll('tbody tr').forEach(function (tr) {
        var r = [];
        tr.querySelectorAll('td').forEach(function (td) { r.push(txt(td)); });
        if (r.length) { body.push(r); }
      });
      doc.setFontSize(9.5); doc.setFont('helvetica', 'bold');
      doc.text(txt(h2), M, y + 2);
      y += 4;
      doc.autoTable({
        head: head, body: body, startY: y,
        margin: { top: M + 17, left: M, right: M, bottom: 16 },
        styles: { font: 'helvetica', fontSize: 6.6, cellPadding: 1.1, overflow: 'linebreak', textColor: 20 },
        headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold', fontSize: 6.6 },
        theme: 'grid'
        /* no didDrawPage: see the note above furniture(). */
      });
      y = doc.lastAutoTable.finalY + 6;
    });

    /* closing notes (the long disclaimer paragraph) */
    var tail = parsed.querySelectorAll('body > p.note');
    if (tail.length) {
      var last = txt(tail[tail.length - 1]);
      doc.setFontSize(7);
      doc.splitTextToSize(last, PW - 2 * M).forEach(function (l) {
        if (y > FOOT - 8) { doc.addPage(); y = HEAD; }
        doc.text(l, M, y); y += 3;
      });
    }
    if (parsed.querySelector('p.note strong') && parsed.querySelector('p.note strong').textContent.indexOf('BLANK TEMPLATE') >= 0) {
      doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text('BLANK TEMPLATE - nothing is recorded on this sheet.', M, HEAD + 4.2);
    }

    /* The single place the header/footer is painted. Runs after the content so the
       real page count is known, and covers EVERY page including ones autoTable
       added. Do not add a second call site. */
    var total = doc.getNumberOfPages();
    for (var pg = 1; pg <= total; pg++) {
      doc.setPage(pg);
      furniture(pg, total);
    }
    return doc;
  }

  function download(which, label) {
    var html = captureHtml(which);
    if (!html || html.indexOf('<table') < 0) {
      window.alert('The sheet could not be built in this browser. Please use the print option instead.');
      return;
    }
    loadLibs(function (err) {
      if (err || !window.jspdf || !window.jspdf.jsPDF) {
        window.alert('The PDF generator could not be loaded (offline or blocked). Please use the print option instead - the sheet itself is generated in your browser either way.');
        return;
      }
      try {
        var doc = buildPdf(html, { blank: which === 'blank' });
        doc.save('CliniEco-sterilization-log-' + today() + '.pdf');
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'tool_export_pdf', { tool: 'compliance_log', blank: which === 'blank' ? 1 : 0 });
          }
        } catch (e) { /* telemetry must never break the export */ }
      } catch (e2) {
        window.alert('The PDF could not be generated in this browser. Please use the print option instead.');
      }
    });
  }

  /* ---- expose + wire the button (injected, so the page template is untouched) ---- */
  function boot() {
    var T = window.cliniecoComplianceLog;
    if (!T) { return; }
    T.pdf = function () { download('filled'); };
    T.pdfBlank = function () { download('blank'); };

    var row = document.querySelector('#clog-hero .clog-hero-btns');
    if (row && !row.querySelector('[data-clog-act="pdf"]')) {
      var a = document.createElement('a');
      a.className = 'clog-btn clog-primary';
      a.href = '#clinieco-log-tool';
      a.setAttribute('data-clog-act', 'pdf');
      a.textContent = 'Download PDF';
      row.insertBefore(a, row.firstChild);
      var first = row.querySelectorAll('[data-clog-act="blank"]')[0];
      if (first) { first.className = first.className.replace(/\s*clog-primary/, ''); }
    }
    /* the base module's hero listener ignores unknown acts, so we own this one */
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t.getAttribute) {
        if (t.getAttribute('data-clog-act') === 'pdf') {
          if (e.preventDefault) { e.preventDefault(); }
          download('filled');
          return;
        }
        t = t.parentNode;
      }
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }
})();
